import React, { useState, useEffect, useRef } from "react";
import axiosInstance from '../services/axiosInstance';
import GlobalTable from "../components/GlobalTable";
import { useLazyTable } from "../hooks/useLazyTable";
import { handleApiError, handleApiSuccess } from "../components/apiErrorHandler";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { AutoComplete } from "primereact/autocomplete";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";

const Inventory = () => {
    const [inventory, setInventory] = useState({
        organization: "b835b027-72ba-4c99-af9c-c8c6c1e1c732",
        product: "",
        quantity: "",
        product_name: "",
    });

    const [inventories, setInventories] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const { lazyParams, onPage, onSort, onFilter, queryParams } = useLazyTable({ rows: 15 });
    const [productsq, setProductsq] = useState([]);
    const [dialogVisible, setDialogVisible] = useState(false);
    const quantityInputRef = useRef(null);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [dialogName, setDialogName] = useState('Create Inventory Item');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const inventoryResult = await axiosInstance.get("/api/inventory/", { params: queryParams });
                setInventories(inventoryResult.data.results);
                setTotalRecords(inventoryResult.data.count);
                setLoading(false);
            } catch (error) {
                handleApiError(error);
                setLoading(false);
            }
        };
        fetchData();
    }, [queryParams]);

    const handleChange = (e, field) => {
        const value = e.target ? e.target.value : e.value;
        setInventory((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleRowClick = (dep) => {
        setInventory(dep);
        setIsReadOnly(true);  // Set to read-only mode
        setDialogVisible(true);
        setDialogName('Inventory Item Details')
    };

    const handleRowEditComplete = async (e) => {
        const { newData, index } = e;

        try {
            const updatedInventory = await axiosInstance.put(`/api/inventory/${newData.id}/`, newData);
            const updatedInventories = [...inventories];
            updatedInventories[index] = updatedInventory.data;
            setInventories(updatedInventories);
            handleApiSuccess("Inventory updated successfully!");
        } catch (error) {
            handleApiError(error);
        }
    };

    const columns = [
        {   Header: "Product", 
            accessor: "product_name", 
            sortable: true, 
            readOnly: true,
            bodyTemplate: (rowData) => (
            <a
                href="/inventory/#"
                onClick={(e) => {
                    e.preventDefault();
                    handleRowClick(rowData);
                }}
            >
                {rowData.product_name}
            </a>
        ),
     },
        { Header: "Quantity", accessor: "quantity", sortable: true, dataType: "numeric", readOnly: true },
        { Header: "Re-Order Level", accessor: "reorder_level", sortable: true, dataType: "numeric" },
        { Header: "Department", accessor: "department_name", sortable: true, readOnly: true},
        { Header: "Expiry Date", accessor: "expiry_date", dataType:"date", filtered:true, sortable: true, readOnly: true,
            bodyTemplate: (rowData) => {
                if (!rowData.expiry_date) return '';
                // Convert string to Date only for display, never store Date objects in state
                const d = rowData.expiry_date instanceof Date
                    ? rowData.expiry_date
                    : new Date(rowData.expiry_date);
                return isNaN(d.getTime()) ? rowData.expiry_date : d.toLocaleDateString('en-GB');
            }
        },
        { Header: "Batch Number", accessor: "batch_number", sortable: true, readOnly: true},
        { 
            Header: "Exceptional Batch", 
            accessor: "is_exceptional", 
            filtered:true, 
            readOnly: true,
            bodyTemplate: (rowData) => (
                            <Checkbox checked={rowData.is_exceptional === true || rowData.is_exceptional === 'Y'} disabled />
                        ),
        },
        { 
            Header: "Active Batch", 
            accessor: "is_active", 
            filtered:true,
            readOnly: true,
            bodyTemplate: (rowData) => (
                            <Checkbox checked={rowData.is_active === true || rowData.is_active === 'Y'} disabled />
                        ),
        },
        { Header: "Purchase Price", accessor: "purchase_price", sortable: true, readOnly: true}
    ];

    return (
        <div>
            <div className="containers">
                <GlobalTable
                    columns={columns}
                    data={inventories}
                    tabletitle="Inventory List"
                    lazy
                    totalRecords={totalRecords}
                    lazyParams={lazyParams}
                    onPage={onPage}
                    onSort={onSort}
                    onFilter={onFilter}
                    customEditors={{
                        reorder_level: (options) => (
                            <InputNumber
                                value={options.rowData.reorder_level}
                                onValueChange={(e) => options.editorCallback(options.rowData.reorder_level = e.value)}
                            />
                        ),
                    }}
                    onRowEditComplete={handleRowEditComplete}
                    loading={loading}
                />
            </div>
            <Dialog
                header={dialogName}
                visible={dialogVisible}
                onHide={() => setDialogVisible(false)}
                modal
                style={{ width: '32rem', borderRadius: '12px', padding: '.5rem'}} 
                breakpoints={{ '960px': '75vw', '641px': '90vw' }}
                className="p-fluid"
            >
                <form id="dialogForm">
                <div style={{ borderRadius: '12px', padding: '1rem' }}>
                <div className="field" style={{ position: "relative" }} >
                    <label htmlFor="product" className="font-bold">Product:</label>
                    <div style={{ position: "relative", display: "flex", alignItems: "center" }}></div>
                    <AutoComplete
                        value={inventory.product_name || ""}
                        suggestions={productsq}
                        completeMethod={(e) => {
                            const query = e.query;
                            axiosInstance
                                .get(`/api/productsq?query=${query}`)
                                .then((res) => {
                                    const processedSuggestions = res.data.map((product) => ({
                                        ...product,
                                        displayLabel: product.name,
                                    }));
                                    setProductsq(processedSuggestions);
                                })
                                .catch((error) => handleApiError(error));
                        }}
                        field="name"
                        onChange={(e) => {
                            if (e.value && typeof e.value === "object") {
                                handleChange({ target: { value: e.value.id } }, "product");
                                handleChange({ target: { value: e.value.name } }, "name");
                            } else {
                                handleChange({ target: { value: e.value } }, "name");
                                handleChange({ target: { value: null } }, "product");
                            }
                        }}
                        placeholder="Select Product"
                        required
                        readOnly={isReadOnly}
                    />
                    </div>
                    <div className="field">
                        <label htmlFor="quantity" className="font-bold">Quantity:</label>
                        <InputNumber
                            ref={quantityInputRef}
                            id="quantity"
                            value={inventory.quantity}
                            onValueChange={(e) => handleChange(e, "quantity")}
                            placeholder="Enter Quantity"
                            required
                            readOnly={isReadOnly}
                        />
                    </div>
                    <div className="field">
                        <label htmlFor="reorder_level" className="font-bold">Re-Order Level:</label>
                        <InputNumber
                            id="reorder_level"
                            value={inventory.reorder_level}
                            onValueChange={(e) => handleChange(e, "reorder_level")}
                            placeholder="Enter Re-Order Level"
                            readOnly={isReadOnly}
                        />
                    </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                        <Button label="Cancel" icon="pi pi-times" outlined onClick={(e) => {setDialogVisible(false); e.preventDefault();}}  style={{borderRadius:"6px", maxWidth: "120px"}} />
                    </div> 
                </form>
            </Dialog>
        </div>
    );
};

export default Inventory;